import AdminLayout from "../../commponents/layout/adminLayout";
import { useEffect, useState } from "react";
import type { TableColumn } from "../../commponents/table/types";
import { Download, Eye, Pencil, Plus, Trash2 } from "lucide-react";
import DataTable from "../../commponents/table/DataTable";
import { deleteQuotationById, getQuotation, getQuotationStatus, quotationPdf } from "../../api/quotationApi";
import { useNavigate } from "react-router-dom";
import QuotationPdfPreviewModal from "../../commponents/quotation/QuotationPdfPreviewModal";
import StatusFilter from "../../commponents/filter/StatusFilter";
import { showConfirm, showError, showSuccess } from "../../utils/ alert";
import { getAuthUser } from "../../api/authApi";
import { isForbiddenError } from "../../lib/axios";
import { isSalesEditLocked, isSalesRole } from "../../lib/roleAccess";

interface QuotationStatus {
  id: number;
  name: string;
  code: string;
  color: string;
}

interface Sales {
  id: number;
  name: string;
}

interface Quotation {
  id: number;
  quotation_no: string;
  recipient_name: string;
  quotation_date: string;
  grand_total: string;

  sales: Sales | null;
  status: QuotationStatus | null;

  createdAt: string;
  updatedAt: string;
}
export interface status {
  id: number;
  name: string;
  code: string;
  color: string;
}

export default function Quotation() {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [quotation, setQuotation] = useState<Quotation[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  // ---- State modal preview PDF ----
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewFilename, setPreviewFilename] = useState<string>("quotation.pdf");
  const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
   const [statusOptions, setStatusOptions] = useState<status[]>([]);

  const navigate = useNavigate();

  // Sales: tidak boleh buat DO, hapus, atau edit quotation yang sudah
  // diproses lebih lanjut (backend menolak 403).
  const isSales = isSalesRole(getAuthUser()?.role);

  const columns: TableColumn<Quotation>[] = [
    { key: "quotation_date", title: "Date" },
    { key: "recipient_name", title: "To" },
    { key: "quotation_no", title: "No" },
    {
      key: "sales",
      title: "Sales",
      render: (row) => row.sales?.name ?? "-",
    },
    {
      key: "status",
      title: "Status",
      render: (row) => (
        <span
          className="font-medium"
          style={{
            color: row.status?.color ?? "#6b7280",
          }}>
          {row.status?.name ?? "-"}
        </span>
      ),
    },

    {
      key: "id",
      title: " ",
      render: (row) => (
        <div className="flex items-center gap-2">
          {!isSales && row.status?.code === "ACCEPTED" && (
            <button
              onClick={() => handleCreateDO(row.id)}
              className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 hover:border-blue-700 border-2 border-blue-100 rounded-lg p-1">
              <Plus size={16} />
              DO
            </button>
          )}
        </div>
      ),
    },
    {
      key: "id",
      title: "Action",
      render: (row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleDetail(row.id)}
            className="rounded-lg bg-blue-100 p-2 text-blue-600 hover:bg-blue-200">
            <Eye size={18} />
          </button>

          <button
            onClick={() => handlePreviewPdf(row)}
            className="rounded-lg bg-blue-100 p-2 text-blue-600 hover:bg-blue-200">
            <Download size={18} />
          </button>

          {isSales && isSalesEditLocked(row.status?.code) ? (
            <button
              disabled
              aria-label="Edit"
              title="Quotation sudah diproses (DO/PO/WO) dan tidak dapat diedit"
              className="cursor-not-allowed rounded-lg bg-gray-100 p-2 text-gray-400">
              <Pencil size={18} />
            </button>
          ) : (
            <button
              onClick={() => handleEdit(row.id)}
              aria-label="Edit"
              className="rounded-lg bg-yellow-100 p-2 text-yellow-600 hover:bg-yellow-200">
              <Pencil size={18} />
            </button>
          )}

          {!isSales && (
            <button
              onClick={() => handleDelete(row.id)}
              aria-label="Delete"
              className="rounded-lg bg-red-100 p-2 text-red-600 hover:bg-red-200">
              <Trash2 size={18} />
            </button>
          )}
        </div>
      ),
    },
  ];

  function handleCreateDO(id: number): void {
    navigate(`/delivery_order/add/${id}`);
  }

  const handlePreviewPdf = async (row: Quotation) => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewUrl(null);
    setPreviewFilename(`${row.quotation_no || "quotation"}.pdf`);

    try {
      const response = await quotationPdf(row.id);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (error) {
      if (!isForbiddenError(error)) showError("Failed to load quotation preview.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
  };

  const handleDetail = (id: number) => {
    navigate(`/quotation/detail/${id}`);
  };

  const fetchQuotation = async () => {
    try {
      setLoading(true);

      const response = await getQuotation({
        page: currentPage,
        limit: pageSize,
        search,
        status
      });

       const dataStatus = await getQuotationStatus()
      
            setStatusOptions(dataStatus.data.quotationStatusList);

      setQuotation(response.data.quotationList);
      setTotalPages(response.data.pagination.totalPages);
    } catch (error) {
      if (!isForbiddenError(error)) showError("Failed", "Failed to Load data.");
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    fetchQuotation();
  }, [currentPage, pageSize, search, status]);

  // Cleanup object URL kalau komponen unmount saat modal masih terbuka
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleDelete = async (id: number) => {
    try {
      const confirmed = await showConfirm(
        "Are you sure you want to delete this Quotation?"
      );
      if (!confirmed) return;

      await deleteQuotationById(id);
      await fetchQuotation();

      await showSuccess("Quotation deleted successfully.");
    } catch (error) {
      if (!isForbiddenError(error)) showError("Failed", "Failed to deleted data.");

    }
  };

  const handleAdd = () => navigate("/quotation/add");

  const handleEdit = (id: number) => navigate(`/quotation/edit/${id}`);
  
  return (
    <AdminLayout>
      <div className="rounded-3xl bg-white p-6">
        <DataTable
          title="Quotation List"
          columns={columns}
          data={quotation}
          onAdd={handleAdd}
          filterContent={
                      <>
                        <StatusFilter
                          value={status}
                          onChange={setStatus}
                          options={statusOptions.map((data) => {
                            return {
                              label: data.name,
                              value: data.code,
                            };
                          })}
                        />
                      </>
                    }
          onSearch={(value) => {
            // FIX: reset ke halaman 1 tiap kali keyword search berubah,
            // supaya tidak "nyangkut" di halaman kosong (mis. lagi di
            // halaman 3 lalu ngetik keyword baru yang hasilnya cuma 1 halaman).
            setSearch(value);
            setCurrentPage(1);
          }}
          pagination={{
            currentPage,
            totalPages,
            pageSize,
            onPageChange: setCurrentPage,
            onPageSizeChange: (size) => {
              setPageSize(size);
              setCurrentPage(1);
            },
          }}
        />

        <QuotationPdfPreviewModal
          open={previewOpen}
          loading={previewLoading}
          pdfUrl={previewUrl}
          filename={previewFilename}
          onClose={handleClosePreview}
        />
      </div>
    </AdminLayout>
  );
}


